/**
 * MappingOutputNode — Custom ReactFlow node for the mapping deliverable.
 *
 * Shown on canvas after pipeline + mapping is complete. Green-themed with
 * lifecycle states derived from batch status:
 *   pending (ready_for_mapping) → running (mapping) → completed (mapped/approved) → failed
 *
 * Visual spec:
 * - Width 240px, white background, green left border
 * - Header: mapping icon + schema name + status badge
 * - Description: lifecycle-dependent ("Awaiting Generation" / "Generating..." / "42/48 mapped")
 * - "Open Editor →" link for portal navigation
 * - Top Handle (from pipeline output), Bottom Handle (for destination)
 */
import React from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Box, Typography, Link, alpha, Button } from '@mui/material';
import {
  Check as CheckIcon,
  TableChart as MappingIcon,
  HourglassEmpty as PendingIcon,
  ErrorOutline as ErrorIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';

export interface MappingOutputNodeData {
  title: string;
  description: string;
  status: string;
  rowCount?: number;
  columnCount?: number;
  /** Batch ID for navigation. */
  batchId?: string;
  /** Current batch status for lifecycle rendering. */
  batchStatus?: string;
  /** Navigation callback for portal behavior. */
  onNavigate?: (path: string) => void;
  /** Callback to open the mapping editor in-place (mode switch, no route change). */
  onOpenEditor?: () => void;
  /** Callback to open schema picker for changing/selecting schema. */
  onChangeSchema?: () => void;
}

const MappingOutputNode: React.FC<NodeProps<MappingOutputNodeData>> = ({
  data,
  selected,
  sourcePosition,
  targetPosition,
}) => {
  const targetHandlePos = targetPosition ?? Position.Top;
  const sourceHandlePos = sourcePosition ?? Position.Bottom;
  const isApproved = data.batchStatus === 'approved';
  const isMapped = data.batchStatus === 'mapped';
  const isGenerating = data.batchStatus === 'mapping';
  const isPending = data.batchStatus === 'ready_for_mapping';
  const isFailed = data.status === 'failed';

  const accentColor = isFailed ? '#dc2626' : '#059669'; // emerald-600

  // Status badge icon
  const statusIcon = isFailed
    ? <ErrorIcon sx={{ fontSize: 14, color: '#fff' }} />
    : (isApproved || isMapped)
      ? <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
      : <PendingIcon sx={{ fontSize: 14, color: '#fff' }} />;

  const handleDoubleClick = () => {
    if (data.onOpenEditor) {
      data.onOpenEditor();
    }
  };

  return (
    <Box
      onDoubleClick={handleDoubleClick}
      sx={{
        width: 240,
        background: '#fff',
        borderRadius: 0.5,
        border: selected
          ? `2px solid ${accentColor}`
          : isPending
            ? `1.5px dashed ${alpha(accentColor, 0.4)}`
            : '1.5px solid #d1fae5',
        borderLeft: `4px solid ${accentColor}`,
        boxShadow: selected
          ? `0 0 0 3px ${alpha(accentColor, 0.15)}`
          : '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        cursor: 'pointer',
        ...(isGenerating && {
          animation: 'mappingPulse 2s ease-in-out infinite',
          '@keyframes mappingPulse': {
            '0%, 100%': { boxShadow: `0 0 0 0px ${alpha(accentColor, 0.1)}` },
            '50%': { boxShadow: `0 0 0 4px ${alpha(accentColor, 0.15)}` },
          },
        }),
      }}
    >
      {/* Target handle */}
      <Handle
        type="target"
        position={targetHandlePos}
        style={{
          background: accentColor,
          width: 8,
          height: 8,
          border: `2px solid ${alpha(accentColor, 0.3)}`,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: alpha(accentColor, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <MappingIcon sx={{ fontSize: 16, color: accentColor }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{
              fontSize: 12,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {data.title}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: 10, color: '#6b7280', fontVariant: 'small-caps', letterSpacing: 0.5 }}
          >
            SCHEMA MAPPING
          </Typography>
        </Box>
        {/* Status badge */}
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: accentColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            ...(isGenerating && {
              animation: 'spinBadge 1.5s linear infinite',
              '@keyframes spinBadge': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }),
          }}
        >
          {statusIcon}
        </Box>
      </Box>

      {/* Description */}
      <Box sx={{ px: 1.5, pb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: 11,
            color: isGenerating ? accentColor : isFailed ? '#dc2626' : '#64748b',
            display: 'block',
            lineHeight: 1.3,
            fontWeight: isGenerating ? 600 : 400,
          }}
        >
          {data.description}
        </Typography>
        {data.rowCount != null && (
          <Typography
            variant="caption"
            sx={{ fontSize: 10, color: '#94a3b8', mt: 0.25, display: 'block' }}
          >
            ⊞ {data.rowCount.toLocaleString()} {data.columnCount != null && `⊟ ${data.columnCount}`}
          </Typography>
        )}
      </Box>

      <Box sx={{ px: 1.5, pb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
        {data.onOpenEditor && !isFailed && (isApproved || isMapped) &&
          <Button
            size="small"
            variant="contained"
            startIcon={<OpenIcon sx={{ fontSize: '12px !important' }} />}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              data.onOpenEditor!();
            }}
            sx={{
              fontSize: 10,
              textTransform: 'none',
              bgcolor: accentColor,
              '&:hover': { bgcolor: isFailed ? '#b91c1c' : '#047857' },
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
              minHeight: 0,
              lineHeight: 1.4,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontWeight: 700,
            }}
          >
            Open Mapping Editor
          </Button>
        }
        {isPending && data.onChangeSchema &&
          <Link
            component="button"
            variant="caption"
            underline="hover"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              data.onChangeSchema!();
            }}
            sx={{
              fontSize: 10,
              color: '#7c3aed',
              cursor: 'pointer',
              fontWeight: 600,
              '&:hover': { color: '#6d28d9' },
            }}
          >
            Select Schema ▸
          </Link>
        }
      </Box>

      {/* Source handle — for destination continuation */}
      <Handle
        type="source"
        position={sourceHandlePos}
        style={{
          background: accentColor,
          width: 8,
          height: 8,
          border: `2px solid ${alpha(accentColor, 0.3)}`,
        }}
      />
    </Box>
  );
};

export default MappingOutputNode;
